import { ServiceException } from '@inter-ikea-kompis/exceptions';
import ServiceFetch from '../../../utilities/ServiceFetch.js';
import ChinaAuthenticationServiceCookieNameEnum from './ChinaAuthenticationServiceCookieNameEnum.js';
/**
 * This service is used for handling authentication in China.
 */
export default class ChinaAuthenticationService {
    constructor(options) {
        this.loginIntervalTime = 500;
        this.refreshTokenEndpoint = options.refreshTokenEndpoint;
        this.loginPageEndpoint = options.loginPageEndpoint;
    }
    /**
     * Authenticates and returns token.
     *
     * @returns Promise.
     */
    async getToken() {
        const accessToken = this.getCookie(ChinaAuthenticationServiceCookieNameEnum.accessToken);
        const refreshToken = this.getCookie(ChinaAuthenticationServiceCookieNameEnum.refreshToken);
        if (accessToken) {
            return accessToken;
        }
        else if (refreshToken) {
            return await this.refreshAccessToken(refreshToken);
        }
        return await this.login();
    }
    /**
     * Request the user to login.
     *
     * @returns Access token.
     */
    login() {
        return new Promise((resolve, reject) => {
            this.removeCookie(ChinaAuthenticationServiceCookieNameEnum.accessToken);
            this.removeCookie(ChinaAuthenticationServiceCookieNameEnum.refreshToken);
            const popup = window.open(this.loginPageEndpoint);
            let intervalId = null;
            intervalId = setInterval(() => {
                const accessToken = this.getCookie(ChinaAuthenticationServiceCookieNameEnum.accessToken);
                if (accessToken) {
                    if (intervalId)
                        clearInterval(intervalId);
                    popup?.close();
                    resolve(accessToken);
                }
                else if (popup?.closed) {
                    reject(new ServiceException('Failed to login. The popup was closed.'));
                }
            }, this.loginIntervalTime);
        });
    }
    /**
     * @returns "true" if access and/or refresh token cookie exists.
     */
    hasCookie() {
        const accessToken = this.getCookie(ChinaAuthenticationServiceCookieNameEnum.accessToken);
        const refreshToken = this.getCookie(ChinaAuthenticationServiceCookieNameEnum.refreshToken);
        return accessToken !== null || refreshToken !== null;
    }
    /**
     * Refreshes the access token.
     *
     * @param refreshToken Refresh token.
     * @returns Access token.
     */
    async refreshAccessToken(refreshToken) {
        const response = await ServiceFetch.json(this.refreshTokenEndpoint, {
            method: 'post',
            credentials: 'same-origin',
            headers: {
                'content-type': 'application/json',
                'x-client-channel': 'WEB',
                'x-client-platform': 'PcWeb',
                source: 'PLANNER'
            },
            body: JSON.stringify({
                refreshToken
            })
        });
        if (!response.data && response.code) {
            if (response.code === 'InvalidParameter.Refresh.Token.Error') {
                return this.login();
            }
            throw new ServiceException(`Failed to refresh access token. Error: ${response.code}`);
        }
        this.setCookie(ChinaAuthenticationServiceCookieNameEnum.accessToken, response.data.id_token, response.data.expires_in * 1000);
        return response.data.id_token;
    }
    /**
     * Removes a cookie.
     *
     * @param name Name.
     */
    removeCookie(name) {
        this.setCookie(name, '', -1000);
    }
    /**
     * Sets a cookie.
     *
     * @param name Name.
     * @param value Value.
     * @param expiresIn Expires in.
     */
    setCookie(name, value, expiresIn) {
        const date = new Date();
        date.setTime(date.getTime() + expiresIn);
        document.cookie = `${name}=${value}; path=/; expires=${date.toUTCString()};`;
    }
    /**
     * Returns a cookie.
     *
     * @param name Name.
     * @returns Cookie value.
     */
    getCookie(name) {
        const cookie = document.cookie.match('(^|;)\\s*' + name + '\\s*=\\s*([^;]+)');
        return cookie?.pop() ?? null;
    }
}
//# sourceMappingURL=ChinaAuthenticationService.js.map